/*
 * WebRTCPage
 * Sesión remota GUI con streaming de video
 */

import { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import { useAgent } from '@/hooks/useAgents';
import { useWebRTC } from '@/hooks/useWebRTC';
import { useTouchInput } from '@/hooks/useTouchInput';
import { FloatingControlBar, type FloatingControlItem } from '@/components/FloatingControlBar';
import { FloatingChatPanel } from '@/components/FloatingChatPanel';
import { ModifierIndicator } from '@/components/ModifierIndicator';
import { SessionRoleBar } from '@/components/SessionRoleBar';
import { DisplayPicker } from '@/components/webrtc/DisplayPicker';
import { MonitorPane } from '@/components/webrtc/MonitorPane';
import { WebRTCStateBadge } from '@/components/webrtc/WebRTCStateBadge';
import type { DisplayInfo } from '@/types/agent';
import { displayCanvasRect, virtualDesktopBounds } from '@/lib/virtualDesktop';
import { useModifierKeys } from '@/hooks/useModifierKeys';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import MouseIcon from '@mui/icons-material/Mouse';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import SwipeIcon from '@mui/icons-material/Swipe';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const SPECIAL_KEYS = new Set([
  'Backspace',
  'Delete',
  'Enter',
  'Tab',
  'Escape',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'F1',
  'F2',
  'F3',
  'F4',
  'F5',
  'F6',
  'F7',
  'F8',
  'F9',
  'F10',
  'F11',
  'F12',
]);

export const WebRTCPage: React.FC = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { data: agent, error: agentError } = useAgent(agentId || null);
  const {
    videoRef,
    registerVideoRef,
    status,
    error,
    authRequired,
    submitPassword,
    role,
    sessionState,
    startSession,
    stopSession,
    setDisplays,
    sendInput,
    enqueueInput,
    takeover,
    lockSession,
    unlockSession,
    requestRemoteClipboard,
    pushClipboardToAgent,
    pullClipboardFromAgent,
    cursorHidden,
    hideCursor,
    showCursor,
    reconnectFailed,
    reconnectAttempt,
    retrySession,
    inputError,
    dismissInputError,
    inputSessionVersion,
    // PR 6: live WebRTC state. Rendered inline as WebRTCStateBadge
    // so the operator can diagnose a "no video" symptom without
    // opening DevTools.
    pcConnectionState,
    pcIceConnectionState,
    pcSignalingState,
    pcIceGatheringState,
    tracksReceived,
    lastTrackLabel,
    lastTrackKind,
  } = useWebRTC(agentId || null, {
    mode: 'gui',
  });

  // PR 2: the picker's selection. Default to the agent's first detected
  // display when available, otherwise the legacy `displayIndex=0`.
  // The user can change it at any time — the picker calls `setDisplays`
  // which kicks off a fresh session.
  const initialDisplaySelection = useMemo<number[]>(() => {
    const first = agent?.displays?.[0];
    if (first) {
      return [first.index];
    }
    return [0];
  }, [agent?.displays]);
  const [selectedDisplays, setSelectedDisplays] = useState<number[]>(initialDisplaySelection);
  // Keep the selection in sync when the agent re-reports its display list
  // (e.g. user reconnects to the agent after a session drops). Without
  // this the picker would keep pointing at a display that no longer
  // exists, and the agent's "display not in session" path would log
  // warnings on every click.
  useEffect(() => {
    const ds = agent?.displays;
    const first = ds?.[0];
    setSelectedDisplays((prev) => {
      // No agent data yet, or no detected displays: keep the legacy
      // single-monitor default so the page still works against older
      // agents that don't report displays.
      if (!ds || ds.length === 0 || !first) return prev;
      const known = new Set(ds.map((d) => d.index));
      const filtered = prev.filter((idx) => known.has(idx));
      if (filtered.length === 0) {
        // Picker's previous selection is now invalid (e.g. user
        // reconnected after the agent rearranged monitors). Fall back
        // to the first detected display so the user sees *something*.
        return [first.index];
      }
      // Same set, same order → no-op (the picker would re-render otherwise).
      if (filtered.length === prev.length && filtered.every((v, i) => v === prev[i])) {
        return prev;
      }
      return filtered;
    });
  }, [agent?.displays]);

  const handleDisplaysChange = useCallback(
    (displays: number[]) => {
      setSelectedDisplays(displays);
      // The hook handles the actual restart: it updates the displays
      // ref so the next offer carries the new list, and (if a session
      // is already up) starts a fresh one with the new selection.
      setDisplays(displays);
    },
    [setDisplays]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const theme = useTheme();
  // Responsive mobile detection — reactivo a resize/orientation.
  // El anterior `useState(isMobileDevice())` solo se evaluaba al montar,
  // así que rotar el teléfono no actualizaba el layout.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { modifiers } = useModifierKeys();

  // Floating chat panel state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [confirmStopOpen, setConfirmStopOpen] = useState(false);

  // Floating control bar visibility — permite ocultar completamente la
  // botonera flotante (FloatingControlBar) para ver el video remoto sin
  // ningún overlay. El toggle vive dentro de la propia botonera (icon
  // "ojo tachado" para ocultar, "ojo" para mostrar). El estado se
  // persiste en localStorage con clave `webrtc.floatingBarHidden` para
  // que la preferencia sobreviva a recargas y cambios de agente — si
  // el usuario la ocultó en una sesión, espera que esté oculta en la
  // próxima. Cuando está oculta, se renderiza un handle minimal en la
  // esquina superior derecha del container para restaurarla.
  const FLOATING_BAR_HIDDEN_KEY = 'webrtc.floatingBarHidden';
  const [floatingBarHidden, setFloatingBarHidden] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(FLOATING_BAR_HIDDEN_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(FLOATING_BAR_HIDDEN_KEY, String(floatingBarHidden));
    } catch {
      // Ignore quota / privacy errors (Safari private mode, etc.)
    }
  }, [floatingBarHidden]);

  const toggleFloatingBar = useCallback(() => {
    setFloatingBarHidden((prev) => !prev);
  }, []);

  // Bug 2 fix (frontend side): sync the browser-side modifier state to the
  // agent whenever it changes OR the session becomes connected. The agent
  // maintains its own modifier state via diff against incoming key events,
  // but the diff starts from an empty state — if the user is already
  // holding a modifier when the session connects, the agent doesn't know
  // until the first key event. Sending an explicit `modifiers` message
  // closes that gap and also handles edge cases like modifier-only events
  // (user releases Shift without typing anything else).
  //
  // PR 3: also depend on `inputSessionVersion`. Every time the input
  // data channel re-opens (including a mid-session reconnect that keeps
  // the React status as 'connected'), the agent releases its held
  // modifiers via ResetInputStateDispatch. We must re-send the current
  // modifier state so the next key event is dispatched in the correct
  // modifier context — otherwise a Shift that the user kept held across
  // a brief disconnect would not be re-applied.
  //
  // The `useModifierKeys` hook already tracks state via window-level
  // keydown/keyup listeners, so this is a single source of truth.
  useEffect(() => {
    if (status !== 'connected') return;
    void enqueueInput(() =>
      sendInput({
        type: 'modifiers',
        alt: modifiers.alt,
        ctrl: modifiers.ctrl,
        shift: modifiers.shift,
        meta: modifiers.meta,
      })
    );
  }, [
    status,
    modifiers.alt,
    modifiers.ctrl,
    modifiers.shift,
    modifiers.meta,
    inputSessionVersion,
    sendInput,
    enqueueInput,
  ]);

  // PR 3: per-pane coordinates. The signature now takes the video
  // element to use as the bounding box, instead of reading from a
  // global videoRef. The touch hook calls this with the video the
  // user actually touched (e.currentTarget on the touch event), so
  // multi-pane grids compute coords against the right tile.
  const getNormalizedCoords = useCallback(
    (video: HTMLVideoElement, clientX: number, clientY: number) => {
      const rect = video.getBoundingClientRect();
      const videoAspect =
        video.videoWidth && video.videoHeight
          ? video.videoWidth / video.videoHeight
          : rect.width / rect.height;
      const containerAspect = rect.width / rect.height;
      let drawWidth: number;
      let drawHeight: number;
      let offsetX = 0;
      let offsetY = 0;
      if (containerAspect > videoAspect) {
        drawHeight = rect.height;
        drawWidth = drawHeight * videoAspect;
        offsetX = (rect.width - drawWidth) / 2;
      } else {
        drawWidth = rect.width;
        drawHeight = drawWidth / videoAspect;
        offsetY = (rect.height - drawHeight) / 2;
      }
      const relX = clientX - rect.left - offsetX;
      const relY = clientY - rect.top - offsetY;
      const x = Math.max(0, Math.min(1, relX / drawWidth));
      const y = Math.max(0, Math.min(1, relY / drawHeight));
      return { x, y };
    },
    []
  );

  // PR 3: map of track-label → video element. The page maintains this
  // by receiving the <video> ref from each MonitorPane via `onVideoRef`.
  // Used by getTarget() below to resolve a touched/clicked video to
  // its track label without the touch/click handlers having to know
  // about the map.
  const paneVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const setPaneVideoRef = useCallback((label: string, el: HTMLVideoElement | null) => {
    if (el) paneVideoRefs.current.set(label, el);
    else paneVideoRefs.current.delete(label);
  }, []);
  const getTarget = useCallback((video: HTMLVideoElement): string | undefined => {
    // The pane sets data-track-label on the wrapping Box. Walking up
    // the tree from the <video> is more robust than maintaining a
    // label→element map (the page already has one for stream routing,
    // but the <video> itself is the most reliable key when the
    // touch handler asks "which pane was this?").
    let node: HTMLElement | null = video;
    while (node && node !== document.body) {
      const label = node.dataset?.trackLabel;
      if (label) return label;
      node = node.parentElement;
    }
    return undefined;
  }, []);

  const { touchMode, setTouchMode, onTouchStart, onTouchMove, onTouchEnd } = useTouchInput({
    status,
    sendInput,
    enqueueInput,
    getNormalizedCoords,
    getTarget,
    setHasFocus,
  });

  const [pwInput, setPwInput] = useState('');

  // Auto-start only on first activation for this agentId (mount / agentId
  // change). After that, the hook's own scheduleReconnect owns all
  // reconnection decisions — NOT this page-level effect.
  //
  // Why: previously this effect re-evaluated on every `status` change and
  // would call startSession() when status flipped to 'idle' (the hook
  // surfaces 'idle' both on clean teardown and on the path that funnels
  // into scheduleReconnect). That duplicated the start call: the
  // page-level effect would race with the 1s-delayed scheduleReconnect,
  // and the second startSession() would send `webrtc.stop` with the
  // just-assigned sessionId, closing the freshly-created PC 1 second
  // later. The agent echoed that back as `webrtc.stopped` with
  // `reason: front_stop`.
  const autoStartAgentIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!agent) return;
    if (authRequired) return;
    if (!agent.connected) return;

    const id = agent.agentId;
    if (autoStartAgentIdRef.current === id) return; // already started for this agent
    autoStartAgentIdRef.current = id;
    startSession('gui');
    // No cleanup. The unmount cleanup lives in the useEffect below with
    // empty deps. This effect intentionally omits `status` from its deps
    // so it only runs on mount/agentId change, not on every status
    // transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.agentId, agent?.connected, authRequired]);

  // Cleanup on unmount only — separate from the auto-start effect above so
  // it doesn't fire on every status change.
  useEffect(() => {
    return () => {
      try {
        stopSession();
      } catch (_e) {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror of `pcRef.current != null` from useWebRTC — kept as a ref mirror
  // for code that still needs to know whether the live RTCPeerConnection is
  // present (focus / input routing, not auto-start). The auto-start effect
  // above no longer reads it.
  const pcAliveRef = useRef(false);
  useEffect(() => {
    if (status === 'connecting' || status === 'connected') {
      pcAliveRef.current = true;
    } else if (status === 'idle' || status === 'error') {
      pcAliveRef.current = false;
    }
  }, [status]);

  // (autoStartInFlightRef is no longer needed; the auto-start effect was
  // simplified to a single mount/agentId-change trigger and the hook's
  // scheduleReconnect owns reconnect decisions.)
  // Restore keyboard bridge focus after (re)connect. Only steal focus when
  // the user is still inside the WebRTC container — never yank focus from a
  // chat input, terminal, or another tab.
  useEffect(() => {
    if (status !== 'connected') return;
    if (!hiddenInputRef.current) return;
    if (!isMobile) return;
    const active = document.activeElement;
    if (active && !containerRef.current?.contains(active)) return;
    try {
      hiddenInputRef.current.focus({ preventScroll: true });
    } catch {}
  }, [status, isMobile]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (
        !containerRef.current?.contains(target) &&
        !videoRef.current?.contains(target) &&
        !hiddenInputRef.current?.contains(target)
      ) {
        setHasFocus(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [hasFocus, videoRef]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') setHasFocus(false);
    };
    const onWindowBlur = () => setHasFocus(false);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (
        containerRef.current?.contains(target) ||
        videoRef.current?.contains(target) ||
        hiddenInputRef.current?.contains(target)
      ) {
        setHasFocus(true);
      } else {
        setHasFocus(false);
      }
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [videoRef]);

  // PR 3: attach native touch listeners to every pane's <video>.
  // The hook handlers read e.currentTarget to know which pane the
  // user touched, so we bind once per pane and let the hook do the
  // per-event routing. Re-runs when selectedDisplays changes (the
  // hook re-creates the listeners with the new element list).
  //
  // We bind via addEventListener with passive:false so the touch
  // hook can call preventDefault and stop the page from scrolling
  // while the user is interacting with a remote monitor.
  useEffect(() => {
    const elements = Array.from(paneVideoRefs.current.values());
    if (elements.length === 0) return;
    elements.forEach((el) => {
      el.addEventListener('touchstart', onTouchStart, { passive: false });
      el.addEventListener('touchmove', onTouchMove, { passive: false });
      el.addEventListener('touchend', onTouchEnd, { passive: false });
    });
    return () => {
      elements.forEach((el) => {
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
      });
    };
  }, [onTouchStart, onTouchMove, onTouchEnd, selectedDisplays]);

  const enterFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (containerRef.current.requestFullscreen) {
        await containerRef.current.requestFullscreen();
      } else if ((containerRef.current as any).webkitRequestFullscreen) {
        await (containerRef.current as any).webkitRequestFullscreen();
      } else if ((containerRef.current as any).webkitEnterFullscreen) {
        await (containerRef.current as any).webkitEnterFullscreen();
      }
      setIsFullscreen(true);
    } catch (err) {
      console.warn('Fullscreen failed:', err);
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if ((document as any).webkitFullscreenElement) {
        await (document as any).webkitExitFullscreen();
      }
    } catch (err) {
      console.warn('Exit fullscreen failed:', err);
    }
    setIsFullscreen(false);
  }, []);

  // Input serialization chain.
  //
  // Provided by useWebRTC and shared across the page (mouse, wheel) and
  // the touch input hook. Every send goes through this chain so the
  // order of dc.send() calls on the WebRTC data channel matches the
  // order in which the user generated the events. Without it, two
  // sendInput calls in flight concurrently can wake from backoff in
  // microtask order (NOT call order), and a `button.down` can end up
  // in the channel ahead of the preceding `move` — making the click
  // land at the wrong pixel. The chain also guarantees that
  // `button.up` cannot fire before its `button.down` even if the user
  // releases the mouse faster than the data channel drains.
  //
  // Implementation lives in the hook so a single chain covers the mouse,
  // wheel, and touch code paths. See useWebRTC.ts for the full rationale
  // and the close-time reset.

  // Mouse-move throttling: native mousemove fires at the input device rate
  // (often 120Hz+ on high-refresh displays). Sending a WebRTC data-channel
  // message for every event saturates the buffer and crowds out button /
  // click events, which is the most common cause of "click does not
  // register after I move the mouse fast". We coalesce to one move per
  // requestAnimationFrame (~60Hz on a 60Hz display) so the data channel
  // always has headroom for clicks.
  //
  // We also keep the *latest* coordinates (not just the first) so the
  // cursor lands where the user is now, not where they were 8ms ago.
  // `buttons` is the DOM PointerEvent bitmask (left=1, right=2,
  // middle=4). It travels with every move so the host can distinguish a
  // hover from a drag. Keep it with the coalesced move; replacing a pending
  // move must never replace its button state with a stale one.
  const pendingMoveRef = useRef<{ x: number; y: number; target: string; buttons: number } | null>(
    null
  );
  const moveRafRef = useRef<number | null>(null);
  const heldButtonsRef = useRef(0);
  const lastMouseRef = useRef<{ x: number; y: number; target: string } | null>(null);

  // A new input-channel version means the agent has reset its synthetic
  // state. Never continue a local drag onto that fresh channel.
  useEffect(() => {
    heldButtonsRef.current = 0;
    if (moveRafRef.current != null) {
      cancelAnimationFrame(moveRafRef.current);
      moveRafRef.current = null;
    }
    pendingMoveRef.current = null;
  }, [inputSessionVersion]);
  const flushMove = useCallback(() => {
    moveRafRef.current = null;
    const p = pendingMoveRef.current;
    pendingMoveRef.current = null;
    if (!p) return;
    if (status !== 'connected' || !hasFocus) return;
    // Enqueue on the input chain so this move is ordered relative to
    // concurrent button events. A click that arrived between this move's
    // RAF and its flush will already be in the chain ahead of us if
    // the click was processed first; otherwise this move goes first.
    // Either way dc.send() order = enqueue order. The `target` field
    // tags the move with the pane's track label so the agent routes
    // it to the right monitor's capture rect.
    lastMouseRef.current = { x: p.x, y: p.y, target: p.target };
    void enqueueInput(() =>
      sendInput({ type: 'move', x: p.x, y: p.y, buttons: p.buttons, target: p.target })
    );
  }, [status, sendInput, hasFocus, enqueueInput]);
  const scheduleMove = useCallback(
    (x: number, y: number, target: string, buttons: number) => {
      pendingMoveRef.current = { x, y, target, buttons };
      if (moveRafRef.current != null) return;
      moveRafRef.current = requestAnimationFrame(flushMove);
    },
    [flushMove]
  );
  // Cancel any in-flight RAF when the component unmounts or the session
  // disconnects, so we never send a stale move after teardown.
  useEffect(() => {
    return () => {
      if (moveRafRef.current != null) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = null;
      }
      pendingMoveRef.current = null;
    };
  }, []);

  // PR 3: per-pane mouse handlers. The MonitorPane passes its own
  // pre-computed (x, y) and the page adds the pane's track label as
  // `target` on the outgoing message. Each pane's video computes
  // coords against its own bounding rect, so clicks land on the
  // right pixel even when the grid is non-uniform.
  const handlePaneMouseMove = useCallback(
    (coords: { x: number; y: number }, target: string, buttons: number) => {
      // A drag can begin before React has committed the focus state set by
      // pointerdown. Never drop its move merely because that state update is
      // still pending.
      if (status !== 'connected' || (!hasFocus && buttons === 0)) return;
      lastMouseRef.current = { x: coords.x, y: coords.y, target };
      scheduleMove(coords.x, coords.y, target, buttons);
    },
    [status, hasFocus, scheduleMove]
  );

  const handlePaneMouseDown = useCallback(
    (
      coords: { x: number; y: number },
      button: 'left' | 'right' | 'middle',
      buttons: number,
      target: string
    ) => {
      // The pane has just focused the container, but this callback runs in
      // the same native event before React commits that state change.
      if (status !== 'connected') return;
      // Cancel any pending throttled move: the move that was about to
      // fire is now stale — we are about to send a fresh move at
      // (coords.x, coords.y) inside the chain, and the stale one
      // would land AFTER button.down and break the click position.
      if (moveRafRef.current != null) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = null;
        pendingMoveRef.current = null;
      }
      // Atomic click: move → button.down are sent in order on the
      // input chain so dc.send(move) happens-before dc.send(button.down)
      // even under backpressure. The cursor is at (x, y) on the
      // target monitor when the down hits the host.
      const bit = button === 'left' ? 1 : button === 'right' ? 2 : 4;
      // Browsers include the just-pressed button in PointerEvent.buttons.
      // Keep a defensive fallback for synthetic events and retain any other
      // held button (e.g. Ctrl-style multi-button gestures).
      const heldBeforeDown = heldButtonsRef.current;
      const held = buttons || heldBeforeDown | bit;
      heldButtonsRef.current = held;
      lastMouseRef.current = { x: coords.x, y: coords.y, target };
      void enqueueInput(async () => {
        // The initial positioning move precedes the native button down, so
        // it must describe the previous mask (normally zero), not a drag.
        await sendInput({
          type: 'move',
          x: coords.x,
          y: coords.y,
          buttons: heldBeforeDown,
          target,
        });
        await sendInput({ type: 'button', button, action: 'down', target });
      });
    },
    [status, sendInput, enqueueInput]
  );

  const handlePaneMouseUp = useCallback(
    (
      coords: { x: number; y: number },
      button: 'left' | 'right' | 'middle',
      buttons: number,
      target: string
    ) => {
      if (status !== 'connected') return;
      // Cancel any pending move: the move RAF might still have a stale
      // (x, y) queued from before the down. Drop it; the up only needs
      // the button release.
      if (moveRafRef.current != null) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = null;
        pendingMoveRef.current = null;
      }
      const bit = button === 'left' ? 1 : button === 'right' ? 2 : 4;
      // PointerUp.buttons no longer includes the released button. Send a
      // final move with the *previous* mask before button.up: this makes a
      // zero-move drag/drop land at the release coordinates as well.
      const heldBeforeUp = heldButtonsRef.current || buttons | bit;
      heldButtonsRef.current = buttons & ~bit;
      lastMouseRef.current = { x: coords.x, y: coords.y, target };
      void enqueueInput(async () => {
        await sendInput({ type: 'move', x: coords.x, y: coords.y, buttons: heldBeforeUp, target });
        await sendInput({ type: 'button', button, action: 'up', target });
      });
    },
    [status, sendInput, enqueueInput]
  );

  // A lost pointer capture, window blur, hidden document, disconnect, or
  // unmount must not leave a button held on the remote desktop. The agent
  // also resets on DataChannel close; this explicit path covers focus loss
  // while the channel stays open.
  const releaseHeldMouseButtons = useCallback(
    (finalPosition?: { x: number; y: number; target: string }) => {
      const held = heldButtonsRef.current;
      if (!held) return;
      if (finalPosition) lastMouseRef.current = finalPosition;
      heldButtonsRef.current = 0;
      if (moveRafRef.current != null) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = null;
        pendingMoveRef.current = null;
      }
      if (status !== 'connected') return;
      const last = lastMouseRef.current;
      const target = last?.target;
      if (!target) return;
      const releases: Array<'left' | 'right' | 'middle'> = [];
      if (held & 1) releases.push('left');
      if (held & 2) releases.push('right');
      if (held & 4) releases.push('middle');
      void enqueueInput(async () => {
        // Preserve the final in-gesture coordinate even for pointercancel or
        // blur. A remote target may only decide a drag entered it at this last
        // point, so release alone is insufficient.
        if (last) {
          await sendInput({
            type: 'move',
            x: last.x,
            y: last.y,
            buttons: held,
            target: last.target,
          });
        }
        for (const button of releases) {
          await sendInput({ type: 'button', button, action: 'up', target });
        }
      });
    },
    [enqueueInput, sendInput, status]
  );

  useEffect(() => {
    const onBlur = () => releaseHeldMouseButtons();
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') releaseHeldMouseButtons();
    };
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      releaseHeldMouseButtons();
    };
  }, [releaseHeldMouseButtons]);

  useEffect(() => {
    if (status !== 'connected') releaseHeldMouseButtons();
  }, [releaseHeldMouseButtons, status]);

  // Captura robusta de teclado con listeners nativos (no React synthetic events)
  useEffect(() => {
    if (status !== 'connected') return;
    // Helper: si el target del evento está dentro del FloatingChatPanel, lo dejamos pasar
    // para que el TextField del chat pueda recibir la tecla (Enter envía, Shift+Enter newline).
    // Sin este check, el typing en el chat se filtraría hacia el host remoto y veríamos
    // caracteres duplicados en la sesión remota.
    const isInsideChatPanel = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false;
      const node = target as Element;
      return (
        typeof node.closest === 'function' && node.closest('[data-floating-chat-panel]') !== null
      );
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isInsideChatPanel(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (!hasFocus) return;
      sendInput({
        type: 'key',
        action: 'down',
        key: e.key,
        code: e.code,
        alt: e.altKey,
        ctrl: e.ctrlKey,
        shift: e.shiftKey,
        meta: e.metaKey,
      });
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isInsideChatPanel(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (!hasFocus) return;
      sendInput({
        type: 'key',
        action: 'up',
        key: e.key,
        code: e.code,
        alt: e.altKey,
        ctrl: e.ctrlKey,
        shift: e.shiftKey,
        meta: e.metaKey,
      });
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [status, sendInput, hasFocus]);

  // Attach native wheel listener with passive:false so we can call preventDefault reliably
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const SCROLL_PX_PER_LINE = 40;
    const SCROLL_LINES_PER_PAGE = 10;
    // Coalesce wheel deltas per-frame so a fast trackpad scroll (which can
    // fire >100 wheel events/sec) does not saturate the data channel.
    let pendingScroll: { dx: number; dy: number; x: number; y: number; target: string } | null =
      null;
    let scrollRaf: number | null = null;
    const flushScroll = () => {
      scrollRaf = null;
      const s = pendingScroll;
      pendingScroll = null;
      if (!s) return;
      if (status !== 'connected' || !hasFocus) return;
      // Enqueue on the input chain so scroll events are ordered relative
      // to mouse moves / clicks (a click that arrives mid-scroll still
      // goes to the host AFTER the scroll currently in the chain, which
      // matches what the user sees on screen).
      void enqueueInput(async () => {
        await sendInput({ type: 'move', x: s.x, y: s.y, target: s.target });
        await sendInput({ type: 'scroll', scrollX: s.dx, scrollY: s.dy, target: s.target });
      });
    };
    const onWheel = (ev: WheelEvent) => {
      if (status !== 'connected' || !hasFocus) return;
      const pane =
        ev.target instanceof Element ? ev.target.closest<HTMLElement>('[data-track-label]') : null;
      const video = pane?.querySelector('video');
      const target = pane?.dataset.trackLabel;
      if (!video || !target) return;
      const { x, y } = getNormalizedCoords(video, ev.clientX, ev.clientY);
      let dx = ev.deltaX || 0;
      let dy = ev.deltaY || 0;
      if (ev.deltaMode === 1) {
        // already in lines
      } else if (ev.deltaMode === 2) {
        dx *= SCROLL_LINES_PER_PAGE;
        dy *= SCROLL_LINES_PER_PAGE;
      } else {
        dx /= SCROLL_PX_PER_LINE;
        dy /= SCROLL_PX_PER_LINE;
      }
      if (!dx && !dy) return;
      // DOM wheel deltaY is positive when the page scrolls down, while both
      // Windows MOUSEEVENTF_WHEEL and CGEvent scroll use positive for up.
      // Convert once at the protocol boundary; horizontal remains positive
      // to the right on the supported host injectors.
      dy = -dy;
      // Coalesce: sum deltas within the same animation frame so the host
      // sees one consolidated scroll instead of 100 tiny ones.
      if (pendingScroll && pendingScroll.target !== target) {
        if (scrollRaf != null) cancelAnimationFrame(scrollRaf);
        flushScroll();
      }
      if (pendingScroll) {
        pendingScroll.dx += dx;
        pendingScroll.dy += dy;
      } else {
        pendingScroll = { dx, dy, x, y, target };
      }
      if (scrollRaf == null) {
        scrollRaf = requestAnimationFrame(flushScroll);
      }
      ev.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel as EventListener);
      if (scrollRaf != null) {
        cancelAnimationFrame(scrollRaf);
        scrollRaf = null;
      }
      pendingScroll = null;
    };
    // PR 3: `selectedDisplays` is in the deps because the wheel
    // handler routes to the first selected display's video. When
    // the user picks a different monitor in the picker we need to
    // re-bind so the next scroll lands on the new target.
  }, [videoRef, status, sendInput, getNormalizedCoords, hasFocus, enqueueInput, selectedDisplays]);

  // Hidden input keyboard bridge: captures virtual keyboard input on mobile
  const handleHiddenKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (status !== 'connected') return;
      if (SPECIAL_KEYS.has(e.key) || e.ctrlKey || e.altKey || e.metaKey) {
        e.preventDefault();
        sendInput({
          type: 'key',
          action: 'down',
          key: e.key,
          code: e.code,
          alt: e.altKey,
          ctrl: e.ctrlKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        });
      }
    },
    [status, sendInput]
  );

  const handleHiddenKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (status !== 'connected') return;
      if (SPECIAL_KEYS.has(e.key) || e.ctrlKey || e.altKey || e.metaKey) {
        sendInput({
          type: 'key',
          action: 'up',
          key: e.key,
          code: e.code,
          alt: e.altKey,
          ctrl: e.ctrlKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        });
      }
    },
    [status, sendInput]
  );

  const handleHiddenInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      if (status !== 'connected') return;
      const nativeEvent = e.nativeEvent as InputEvent;
      // Skip deletion (handled by keydown) and intermediate composition
      if (
        nativeEvent.inputType?.startsWith('delete') ||
        nativeEvent.inputType === 'insertCompositionText'
      )
        return;
      const data = nativeEvent.data;
      if (data) {
        sendInput({ type: 'text', text: data });
      }
      (e.target as HTMLInputElement).value = '';
    },
    [status, sendInput]
  );

  const handleHiddenCompositionEnd = useCallback(
    (e: React.CompositionEvent<HTMLInputElement>) => {
      if (status !== 'connected') return;
      if (e.data) {
        sendInput({ type: 'text', text: e.data });
      }
      (e.target as HTMLInputElement).value = '';
    },
    [status, sendInput]
  );

  // Enviar Ctrl+Alt+Del — no requiere hasFocus (acción explícita por botón)
  const sendCtrlAltDel = useCallback(() => {
    if (status !== 'connected') return;
    sendInput({ type: 'shortcut', name: 'ctrl_alt_del' });
  }, [status, sendInput]);

  // Enviar Alt+F4 — no requiere hasFocus (acción explícita por botón)
  const sendAltF4 = useCallback(() => {
    if (status !== 'connected') return;
    sendInput({ type: 'shortcut', name: 'alt_f4' });
  }, [status, sendInput]);

  // Enviar acciones especiales — no requieren hasFocus (acción explícita por botón)
  const sendSpecialAction = useCallback(
    (name: string) => {
      if (status !== 'connected') return;
      sendInput({ type: 'shortcut', name });
    },
    [status, sendInput]
  );

  // Abrir teclado virtual (móvil) — no requiere hasFocus previo
  const openVirtualKeyboard = useCallback(() => {
    if (!hiddenInputRef.current) return;
    setHasFocus(true);
    hiddenInputRef.current.value = '';
    hiddenInputRef.current.focus();
    if ('virtualKeyboard' in navigator) {
      try {
        (navigator as any).virtualKeyboard?.show?.();
      } catch {}
    }
  }, []);

  // Construir items de la barra flotante unificada
  const floatingControlItems = useMemo<FloatingControlItem[]>(() => {
    const baseItems: FloatingControlItem[] = [
      {
        id: 'stop-session',
        icon: <CloseIcon />,
        title: 'Detener sesión',
        onClick: () => setConfirmStopOpen(true),
      },
      {
        id: 'fullscreen-toggle',
        icon: isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />,
        title: isFullscreen ? 'Salir pantalla completa' : 'Pantalla completa',
        onClick: () => (isFullscreen ? exitFullscreen() : enterFullscreen()),
      },
      {
        id: 'controls-toggle',
        // VisibilityOffIcon (ojo tachado) cuando la barra está visible →
        // "click para ocultar". La barra siempre se ve a sí misma (este
        // botón está dentro de ella), así que el "mostrar" se hace
        // desde el handle minimal que aparece en la esquina del
        // container cuando está oculta.
        icon: <VisibilityOffIcon />,
        title: 'Ocultar controles',
        onClick: () => toggleFloatingBar(),
      },
      {
        id: 'chat-toggle',
        icon: chatOpen ? <ChatBubbleIcon /> : <ChatBubbleOutlineIcon />,
        title: chatOpen ? 'Cerrar chat' : 'Abrir chat',
        onClick: () => {
          if (chatOpen) {
            // Si está abierto, el click funciona como cerrar
            setChatOpen(false);
            setChatMinimized(false);
          } else {
            // Si está cerrado, abrir expandido
            setChatOpen(true);
            setChatMinimized(false);
          }
        },
      },
    ];

    if (status === 'connected') {
      // Acciones especiales (disponible desktop + mobile)
      baseItems.push({
        id: 'shortcuts',
        icon: <KeyboardIcon />,
        title: 'Acciones especiales',
        onClick: () => {},
        menuItems: [
          {
            id: 'menu-ctrl-alt-del',
            title: 'Ctrl+Alt+Del',
            shortcut: 'Ctrl+Alt+Supr',
            onClick: sendCtrlAltDel,
          },
          {
            id: 'menu-alt-f4',
            title: 'Alt+F4 (Cerrar ventana)',
            shortcut: 'Alt+F4',
            onClick: sendAltF4,
          },
          {
            id: 'menu-win-r',
            title: 'Win+R (Ejecutar)',
            shortcut: 'Win+R',
            onClick: () => sendSpecialAction('win+r'),
          },
          {
            id: 'menu-win-d',
            title: 'Win+D (Escritorio)',
            shortcut: 'Win+D',
            onClick: () => sendSpecialAction('win+d'),
          },
          {
            id: 'menu-win-l',
            title: 'Win+L (Bloquear)',
            shortcut: 'Win+L',
            onClick: () => sendSpecialAction('win+l'),
          },
          {
            id: 'menu-ctrl-shift-esc',
            title: 'Task Manager',
            shortcut: 'Ctrl+Shift+Esc',
            onClick: () => sendSpecialAction('ctrl+shift+esc'),
          },
          {
            id: 'menu-ctrl-c',
            title: 'Ctrl+C (Copiar)',
            shortcut: 'Ctrl+C',
            onClick: () => sendSpecialAction('ctrl+c'),
          },
          {
            id: 'menu-ctrl-v',
            title: 'Ctrl+V (Pegar)',
            shortcut: 'Ctrl+V',
            onClick: () => sendSpecialAction('ctrl+v'),
          },
        ],
      });

      // Teclado virtual — solo mobile
      if (isMobile) {
        baseItems.push({
          id: 'virtual-keyboard',
          icon: <KeyboardIcon />,
          title: 'Abrir teclado',
          onClick: openVirtualKeyboard,
        });

        // Toggle modo touch (direct ↔ trackpad) — solo mobile
        baseItems.push({
          id: 'touch-mode',
          icon: touchMode === 'direct' ? <TouchAppIcon /> : <SwipeIcon />,
          title: touchMode === 'direct' ? 'Modo: Toque directo' : 'Modo: Trackpad',
          onClick: () => setTouchMode(touchMode === 'direct' ? 'trackpad' : 'direct'),
        });
      }

      // Cursor visibility toggle — solo para controller
      if (role === 'controller' || sessionState === null) {
        baseItems.push({
          id: 'cursor-toggle',
          icon: cursorHidden ? <MouseIcon /> : <VisibilityOffIcon />,
          title: cursorHidden ? 'Mostrar cursor local' : 'Ocultar cursor local',
          onClick: () => (cursorHidden ? showCursor() : hideCursor()),
        });
      }

      // Clipboard requires control, including reads.
      if (role !== 'observer') {
        baseItems.push({
          id: 'clipboard-sync',
          icon: <IntegrationInstructionsIcon />,
          title: 'Sincronizar portapapeles',
          onClick: async () => {
            try {
              if (status !== 'connected') return;
              const localP =
                (navigator as any).clipboard?.readText?.() ??
                Promise.reject(new Error('Clipboard API no disponible'));
              const remoteP = requestRemoteClipboard();
              const [localRes, remoteRes] = await Promise.allSettled([localP, remoteP]);
              const localText = localRes.status === 'fulfilled' ? localRes.value || '' : '';
              const remoteText = remoteRes.status === 'fulfilled' ? remoteRes.value || '' : '';

              if (localText === remoteText) {
                try {
                  window.alert('Portapapeles ya sincronizados');
                } catch {}
                return;
              }

              const copyLocalToRemote = window.confirm(
                'Portapapeles diferentes. ¿Copiar local → remoto? (Aceptar)\nCancelar: copiar remoto → local'
              );
              if (copyLocalToRemote) {
                try {
                  await pushClipboardToAgent();
                } catch (_e) {
                  try {
                    sendInput({ type: 'clipboard.set', text: localText });
                  } catch {}
                }
              } else {
                try {
                  await pullClipboardFromAgent();
                } catch (_e) {
                  try {
                    await (navigator as any).clipboard.writeText(remoteText || '');
                  } catch {}
                }
              }
            } catch (_e) {
              try {
                window.alert(
                  'Error sincronizando portapapeles: ' +
                    (_e instanceof Error ? _e.message : String(_e))
                );
              } catch {}
            }
          },
        });
      }
    }

    return baseItems;
  }, [
    isFullscreen,
    exitFullscreen,
    enterFullscreen,
    toggleFloatingBar,
    isMobile,
    status,
    sendCtrlAltDel,
    sendAltF4,
    sendSpecialAction,
    openVirtualKeyboard,
    touchMode,
    setTouchMode,
    sendInput,
    requestRemoteClipboard,
    pushClipboardToAgent,
    pullClipboardFromAgent,
    role,
    sessionState,
    cursorHidden,
    hideCursor,
    showCursor,
    chatOpen,
  ]);

  const handleSubmitPassword = () => {
    const pw = pwInput.trim();
    if (!pw) return;
    setPwInput('');
    submitPassword(pw);
  };

  // PR 3: layout helpers for the multi-pane grid. The functions below
  // return CSS grid template strings / flex weights that depend only
  // on the number of selected displays, so they're pure and easy to
  // unit-test in isolation. The goal is "looks like TeamViewer on
  // desktop, looks like a vertical stack on mobile" without dragging
  // in a layout library.
  const getGridColumns = (n: number): string => {
    if (n <= 1) return '1fr';
    if (n === 2) return '1fr 1fr';
    if (n === 3) return '1fr 1fr'; // 3rd pane wraps to the next row
    return 'repeat(auto-fit, minmax(280px, 1fr))';
  };
  const getGridRows = (n: number): string => {
    if (n <= 1) return '1fr';
    if (n === 2) return '1fr';
    if (n === 3) return '1fr 1fr';
    if (n === 4) return '1fr 1fr';
    return 'auto';
  };
  const getPaneFlex = (n: number, _index: number): number => {
    if (n <= 1) return 1;
    if (n === 2) return 1; // equal width
    if (n === 3 && _index === 2) return 2; // bottom pane spans both columns
    return 1;
  };
  // sizeHintForDisplay returns the "1920×1080" hint the pane shows on
  // its label badge. Looked up from the agent's reported display list;
  // returns undefined if the agent hasn't reported dimensions (older
  // agents or agents with no displays).
  const sizeHintForDisplay = (
    displays: DisplayInfo[] | undefined,
    displayIndex: number
  ): string | undefined => {
    const d = displays?.find((x) => x.index === displayIndex);
    if (!d || !d.width || !d.height) return undefined;
    return `${d.width}×${d.height}`;
  };
  // PR 3: tracks are labeled "screen-N" by the agent (see
  // media.ScreenTrackLabel on the agent). We mirror that contract
  // here so the page can construct track labels without going through
  // the agent. Keeping it in one place makes refactors safer — if the
  // agent ever renames the prefix, this is the only spot to change
  // (plus a server-side regen of the agent binary).
  const ScreenTrackLabel = (displayIndex: number): string => `screen-${displayIndex}`;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={{ xs: 1, sm: 2 }}
      sx={{
        // Fill the <main> container (which already accounts for the AppBar via
        // <Toolbar /> spacer). Antes había `height: '100vh'` + un
        // `maxHeight: 'calc(100vh - 115px)'` hardcodeado que se contradecía
        // con el padding del MainLayout y generaba gaps raros en mobile.
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        p: { xs: 1, sm: 2 },
      }}
    >
      {/* Bug 4: input error banner. Surfaces persistent input failures
          from the agent. The first error shows immediately; subsequent
          failures update the banner with the running count every 50
          events or 60s (whichever first), so we don't spam the user
          with one toast per failed click. */}
      {inputError && (
        <Alert
          severity="warning"
          onClose={dismissInputError}
          sx={{ flexShrink: 0 }}
          data-testid="input-error-banner"
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Input no llega al equipo remoto ({inputError.action})
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {inputError.message}
            {inputError.count > 1 && (
              <>
                {' — '}
                {inputError.count} errores
                {inputError.sampled ? ' (continúa)' : ''}
              </>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Posibles causas: Secure Desktop / UAC activo, servicio sin permisos, o relay colgado. Si
            persiste, abrí el DebugPanel para más detalle.
          </Typography>
        </Alert>
      )}
      <Dialog open={authRequired} onClose={() => navigate('/agents')} maxWidth="xs" fullWidth>
        <DialogTitle>Contraseña requerida</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Este equipo pide una contraseña para permitir el acceso. Ingresá la contraseña de sesión
            (la que ve la persona en su pantalla) o la contraseña de acceso desatendido.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Contraseña"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmitPassword();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/agents')}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmitPassword} disabled={!pwInput.trim()}>
            Conectar
          </Button>
        </DialogActions>
      </Dialog>
      <ModifierIndicator modifiers={modifiers} />

      {/* Input oculto para captura de teclado virtual en mobile */}
      {isMobile && (
        <input
          ref={hiddenInputRef}
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          style={{
            position: 'fixed',
            top: -1000,
            left: -1000,
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: 'none',
          }}
          aria-hidden="true"
          onKeyDown={handleHiddenKeyDown}
          onKeyUp={handleHiddenKeyUp}
          onInput={handleHiddenInput}
          onCompositionEnd={handleHiddenCompositionEnd}
        />
      )}

      <Box
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={{ xs: 1, sm: 2 }}
        flexShrink={0}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        <Typography
          variant={isMobile ? 'h6' : 'h4'}
          component="h1"
          noWrap
          sx={{ minWidth: 0, flex: '1 1 auto' }}
        >
          {agent?.hostname || agent?.agentId || agentId}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            ml: { xs: 0, sm: 1 },
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            rowGap: 0.5,
          }}
        >
          {isMobile ? (
            // Compact info on mobile: un solo chip con SO + versión
            // (el agentId ya está visible como heading fallback).
            <Chip
              size="small"
              label={`${agent?.os || '—'} · v${agent?.version || '—'}`}
              variant="outlined"
            />
          ) : (
            <>
              <Chip label={`SO: ${agent?.os || '—'}`} variant="outlined" />
              <Chip label={`Versión: ${agent?.version || '—'}`} variant="outlined" />
              <Chip label={`ID: ${agent?.agentId || agentId || '—'}`} variant="outlined" />
            </>
          )}
          <SessionRoleBar
            role={role}
            sessionState={sessionState}
            onTakeover={takeover}
            onLock={lockSession}
            onUnlock={unlockSession}
          />
        </Stack>
      </Box>

      {/* PR 2: multi-monitor picker. Sits below the agent header so it's
          always visible without scrolling. The picker is hidden when
          the agent hasn't reported displays yet (legacy agents) — the
          single-monitor path keeps working through the rest of the
          page. */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <DisplayPicker
          displays={agent?.displays}
          displaysError={agent?.displaysError}
          selected={selectedDisplays}
          onChange={handleDisplaysChange}
          disabled={status === 'connecting'}
        />
      </Box>

      {/* PR 6: live WebRTC state badge. Shows connectionState /
          iceConnectionState / signalingState / iceGatheringState +
          tracks received so an operator can diagnose "no video"
          without opening DevTools. Hidden until a PeerConnection is
          actually created (the component itself returns null when
          every state is null), so it doesn't add noise to the
          pre-connect flow. */}
      <Box sx={{ flexShrink: 0 }}>
        <WebRTCStateBadge
          pcConnectionState={pcConnectionState}
          pcIceConnectionState={pcIceConnectionState}
          pcSignalingState={pcSignalingState}
          pcIceGatheringState={pcIceGatheringState}
          tracksReceived={tracksReceived}
          lastTrackLabel={lastTrackLabel}
          lastTrackKind={lastTrackKind}
        />
      </Box>

      {agentError && (
        <Alert severity="error" sx={{ flexShrink: 0 }}>
          Error cargando agente: {agentError instanceof Error ? agentError.message : 'Error'}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ flexShrink: 0 }}>
          {error}
        </Alert>
      )}
      {reconnectFailed && (
        <Alert
          severity="warning"
          sx={{ flexShrink: 0 }}
          action={
            <Button color="inherit" size="small" onClick={() => void retrySession()}>
              Reintentar
            </Button>
          }
        >
          Sesión interrumpida: no se pudo reconectar automáticamente. Hacé clic en
          &quot;Reintentar&quot; para reanudar la conexión.
        </Alert>
      )}
      {status === 'reconnecting' && !reconnectFailed && (
        <Alert severity="info" sx={{ flexShrink: 0 }}>
          {error || 'La sesión se interrumpió. Intentando reconectar automáticamente…'}
          {reconnectAttempt > 0 ? ` Intento ${reconnectAttempt} de 5.` : ''}
        </Alert>
      )}

      <Paper
        sx={{
          p: { xs: 1, sm: 2 },
          flex: 1,
          minHeight: 0,
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1, sm: 2 },
          overflow: 'hidden',
        }}
      >
        <Box
          ref={containerRef}
          tabIndex={0}
          onClick={(event) => {
            // The chat lives in this container: keeping its input focused is
            // essential so text never gets sent to the remote desktop.
            if ((event.target as Element).closest('[data-floating-chat-panel]')) return;
            containerRef.current?.focus();
          }}
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            maxHeight: '100%',
            borderRadius: 0.5,
            overflow: 'hidden',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : theme.palette.common.black,
            border: '1px solid',
            borderColor: hasFocus ? 'primary.main' : 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/*
            Botonera flotante principal. Se renderiza solo si el usuario
            no la ocultó explícitamente (estado persistido en
            `webrtc.floatingBarHidden`). Cuando está oculta, se muestra
            un handle minimal en la misma esquina (top-right) para
            poder restaurarla. El handle se posiciona absolute dentro
            del container, igual que la FloatingControlBar.
          */}
          {!floatingBarHidden && (
            <FloatingControlBar
              anchorRef={containerRef}
              items={floatingControlItems}
              position="top-right"
              zIndex={1200}
              tooltipContainer={containerRef.current}
            />
          )}

          {floatingBarHidden && (
            <Tooltip title="Mostrar controles" placement="left" arrow>
              <IconButton
                onClick={() => setFloatingBarHidden(false)}
                aria-label="Mostrar controles"
                size="small"
                data-testid="show-floating-bar-handle"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 1200,
                  backgroundColor: (theme) => alpha(theme.palette.common.black, 0.4),
                  backdropFilter: 'blur(4px)',
                  color: (theme) => theme.palette.common.white,
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.common.white, 0.1),
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.08)',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.9),
                    color: (theme) => theme.palette.common.white,
                  },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <FloatingChatPanel
            agentId={agentId || ''}
            hostname={agent?.hostname || agent?.agentId || agentId || ''}
            open={chatOpen}
            minimized={chatMinimized}
            onClose={() => {
              setChatOpen(false);
              setChatMinimized(false);
            }}
            onToggleMinimize={() => setChatMinimized((v) => !v)}
            anchorRef={containerRef}
          />

          {/* One MonitorPane per selected display. In "Todos", panes use
              host virtual-desktop geometry instead of a responsive grid. */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: selectedDisplays.length > 1 ? 'block' : 'grid',
              padding: selectedDisplays.length > 1 ? 0 : { xs: 0.5, sm: 1 },
              gridTemplateColumns:
                selectedDisplays.length > 1 ? undefined : getGridColumns(selectedDisplays.length),
              gridTemplateRows:
                selectedDisplays.length > 1 ? undefined : getGridRows(selectedDisplays.length),
              overflow: 'hidden',
              pointerEvents: status === 'connected' ? 'auto' : 'none',
            }}
          >
            {selectedDisplays.map((displayIndex, i) => {
              const trackLabel = ScreenTrackLabel(displayIndex);
              const display = agent?.displays?.find((d) => d.index === displayIndex);
              const selectedTopology =
                agent?.displays?.filter((d) => selectedDisplays.includes(d.index)) ?? [];
              const bounds =
                selectedDisplays.length > 1 ? virtualDesktopBounds(selectedTopology) : null;
              const canvasRect = display && bounds ? displayCanvasRect(display, bounds) : undefined;
              const sizeHint = sizeHintForDisplay(agent?.displays, displayIndex);
              return (
                <MonitorPane
                  key={trackLabel}
                  trackLabel={trackLabel}
                  displayIndex={displayIndex}
                  sizeHint={sizeHint}
                  flex={getPaneFlex(selectedDisplays.length, i)}
                  canvasRect={canvasRect}
                  registerVideoRef={registerVideoRef}
                  onMouseDown={(coords, button, buttons) =>
                    handlePaneMouseDown(coords, button, buttons, trackLabel)
                  }
                  onMouseUp={(coords, button, buttons) =>
                    handlePaneMouseUp(coords, button, buttons, trackLabel)
                  }
                  onMouseMove={(coords, buttons) =>
                    handlePaneMouseMove(coords, trackLabel, buttons)
                  }
                  onMouseCancel={(coords) =>
                    releaseHeldMouseButtons({ x: coords.x, y: coords.y, target: trackLabel })
                  }
                  onFocus={() => setHasFocus(true)}
                  onVideoRef={(el) => setPaneVideoRef(trackLabel, el)}
                  disabled={role === 'observer' || status !== 'connected'}
                  active={status === 'connected'}
                />
              );
            })}
          </Box>

          {status === 'connected' && !hasFocus && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.common.black, 0.6),
                  color: (theme) => theme.palette.common.white,
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  textAlign: 'center',
                  maxWidth: 480,
                }}
              >
                {isMobile
                  ? 'Tocá la pantalla para interactuar con el host remoto.'
                  : 'Hacé foco en el video para que los eventos de teclado y mouse se envíen al host.'}
              </Typography>
            </Box>
          )}

          {status !== 'connected' && (
            <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
              {status === 'connecting' || status === 'reconnecting' ? (
                <CircularProgress size={32} />
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {status === 'connecting'
                  ? 'Negociando WebRTC… (máximo 15 segundos)'
                  : status === 'reconnecting'
                    ? error || 'Reconectando la sesión…'
                    : 'Esperando sesión. Iniciá la conexión para ver la pantalla remota.'}
              </Typography>
              {error && status === 'error' && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, textAlign: 'center', maxWidth: 300 }}
                >
                  {error}
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Paper>
      <Dialog
        open={confirmStopOpen}
        onClose={() => setConfirmStopOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>¿Detener la sesión activa?</DialogTitle>
        <DialogContent>
          <Typography>Se cerrará la conexión remota y se detendrán los reintentos.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStopOpen(false)}>Continuar sesión</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmStopOpen(false);
              stopSession();
              navigate('/agents');
            }}
          >
            Detener sesión
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
